import {
    Button,
    DialogAwareButtonContainer,
    DialogAwareContentContainer,
    DialogContent,
    TokenAmount,
} from "@/components";
import { useDialogPortal, useWithdrawLockTransaction } from "@/hooks";
import { TOKEN_IDS } from "@/types";
import { Divider } from "@/components";
import { useTokenApprovalV2 } from "@/hooks/transaction/useTokenApprovalV2";
import { getGatewayContractAddress, getLockAddress } from "@/config";
import { mainnet } from "viem/chains";
import { useCallback, useMemo, useState } from "react";
import { ActionText } from "@/constants/text.enum";
import { getCurrentEpoch, getEpochTimestamp } from "@/utils";
import { useInterval } from "usehooks-ts";
import { safaryTrackStartExitSuccess } from "@/services/analytics/safary.service";
import { useUserTvl } from "@/hooks/infinifi/useUserTvl";
import { useAccount } from "wagmi";
import { useTrackingConsent } from "../../hooks/shared/tracking-consent/useTrackingConsent";
import { PosthogHelper } from "@/posthog/posthog-helper";
import { PostHogEvent } from "@/posthog/posthog-capture-names";
import { getTransactionLoadingState } from "./transaction-loading.utils";
import { Checkbox } from "@mui/material";

export type WithdrawLockTransactionProps = {
    lockDuration: number;
    withdrawLockAmount: number;
};

export function WithdrawLockTransaction({ lockDuration, withdrawLockAmount }: WithdrawLockTransactionProps) {
    const { closeDialog } = useDialogPortal();
    const { address } = useAccount();
    const { liUsdBalanceByDuration } = useUserTvl();

    const {
        gasPriceUSD,
        approvalAmount,
        executeWithdrawLockTransaction,
        isLoading: isWithdrawLockLoading,
        isExecuting,
    } = useWithdrawLockTransaction({
        withdrawLockAmount,
        lockDuration,
    });

    const { isApprovalLoading, canStart, withApproval, approvalLoadingText } = useTokenApprovalV2({
        tokenId: TOKEN_IDS.LIUSD,
        tokenAddress: getLockAddress(mainnet.id, lockDuration),
        amount: approvalAmount,
        spender: getGatewayContractAddress(mainnet.id),
    });

    const { canTrack } = useTrackingConsent();
    const handleConfirm = useCallback(async () => {
        if (!canStart({ isExecuting })) return;

        // handle successful click track
        try {
            PosthogHelper.capture({
                event_name: PostHogEvent.WithdrawLockTransactionDialog.StartWithdrawalButton.clicked,
                properties: {
                    lockDuration,
                    withdrawLockAmount,
                    userAddress: address,
                },
                userIdentifier: address!,
                allowTemporaryOptIn: false,
            });
        } catch (error) {
            console.error("Error capturing posthog event:", error);
        }

        let shouldCloseDialog = false;

        // try to execute withdraw lock transaction
        try {
            const tx = await withApproval(
                async () => {
                    shouldCloseDialog = true;
                    return executeWithdrawLockTransaction();
                },
                { isExecuting },
            );

            if (!tx) return;

            if (tx?.cancelled) throw new Error("User cancelled the transaction.");
            if (canTrack) {
                // safary
                try {
                    safaryTrackStartExitSuccess({
                        duration: lockDuration,
                        tokenAmountLockedInBucket: liUsdBalanceByDuration(lockDuration),
                        tokenAmountOut: withdrawLockAmount,
                        walletAddress: address!,
                    });
                } catch (error) {
                    console.error("Safary error tracking start exit success:", error);
                }

                // posthog
                try {
                    PosthogHelper.capture({
                        event_name: PostHogEvent.WithdrawLockTransactionDialog.WithdrawalTransaction.success,
                        properties: {
                            lockDuration,
                            withdrawLockAmount,
                            userAddress: address,
                            txHash: tx?.receipt,
                        },
                        userIdentifier: address!,
                        allowTemporaryOptIn: false,
                    });
                } catch (error) {
                    console.error("Posthog error tracking withdrawal transaction success:", error);
                }
            }
        } catch (e) {
            console.error("Error executing withdraw lock transaction:", e);
            // capture failure event
            try {
                PosthogHelper.capture({
                    event_name: PostHogEvent.WithdrawLockTransactionDialog.WithdrawalTransaction.failure,
                    properties: {
                        lockDuration,
                        withdrawLockAmount,
                        userAddress: address,
                        errorMessage: (e as Error).message,
                    },
                    userIdentifier: address!,
                    allowTemporaryOptIn: false,
                });
            } catch (error) {
                console.error("Error capturing posthog failure event:", error);
            }
        } finally {
            if (shouldCloseDialog) closeDialog();
        }
    }, [
        canStart,
        withApproval,
        closeDialog,
        lockDuration,
        withdrawLockAmount,
        address,
        canTrack,
        liUsdBalanceByDuration,
    ]);

    const { isLoading, loadingText } = getTransactionLoadingState({
        approvalLoadingText,
        isApprovalLoading,
        isExecuting,
        actionLoadingText: ActionText.STARTING_WITHDRAWAL,
        isTransactionLoading: isWithdrawLockLoading,
    });
    const isDisabled = isLoading || !withdrawLockAmount;
    const [isAcknowledged, setIsAcknowledged] = useState(false);

    const [timeUntilNextEpoch, setTimeUntilNextEpoch] = useState<number>();

    const ddhhmmDisplay = useMemo(() => {
        if (!timeUntilNextEpoch) return "";
        const days = Math.floor(timeUntilNextEpoch / 1000 / 60 / 60 / 24);
        const hours = Math.floor((timeUntilNextEpoch / 1000 / 60 / 60) % 24);
        const minutes = Math.floor((timeUntilNextEpoch / 1000 / 60) % 60);
        return `${days.toString().padStart(2, "0")}d ${hours.toString().padStart(2, "0")}h ${minutes.toString().padStart(2, "0")}m`;
    }, [timeUntilNextEpoch]);

    useInterval(() => {
        setTimeUntilNextEpoch(getEpochTimestamp(getCurrentEpoch() + 1) - Date.now());
    }, 1000);

    const disclaimer = `I acknowledge that unlocking now means I'll forfeit yields for the remainder of this epoch${
        ddhhmmDisplay ? ` (${ddhhmmDisplay} remaining).` : "."
    } To maximize returns, I can wait and begin my unlock nearer to the end of the current epoch.`;

    const isConfirmDisabled = isDisabled || !timeUntilNextEpoch || !isAcknowledged;

    return (
        <DialogContent className="min-w-sm w-[420px] max-w-xl">
            <DialogAwareContentContainer isDialog className="gap-4">
                <div className="space-y-2">
                    <div className="flex items-center justify-between text-[14px] leading-8 text-dark">
                        <span>Unlock</span>
                        <div className="flex-col flex">
                            {/* 
                            
                            need ui approval
                            <TokenAmount
                                amount={Big(approvalAmount).neg().toFixed(6, 0)}
                                tokenId={TOKEN_IDS.LIUSD}
                                suffix={"-" + lockDuration + "w"}
                                size="md"
                            /> 
                            */}
                            <TokenAmount amount={withdrawLockAmount} tokenId={TOKEN_IDS.IUSD} size="md" />
                        </div>
                    </div>
                    <div className="flex items-center justify-between text-[14px] leading-8 text-dark">
                        <span>Duration</span>
                        <span>
                            {lockDuration} week{lockDuration === 1 ? "" : "s"}
                        </span>
                    </div>
                </div>
                <Divider />
                <div className="flex items-center justify-between text-[12px] leading-7 text-dark">
                    <span>Network Fee</span>
                    <span>${gasPriceUSD}</span>
                </div>
            </DialogAwareContentContainer>

            <DialogAwareButtonContainer isDialog className="gap-3">
                <label className="flex items-start gap-2 text-[12px] leading-6 text-dark mb-5">
                    <Checkbox
                        checked={isAcknowledged}
                        onChange={(e) => setIsAcknowledged(e.target.checked)}
                        className="!p-0.5"
                        disabled={isLoading || !timeUntilNextEpoch}
                    />
                    <span className="pt-0.5">{disclaimer}</span>
                </label>

                <Button
                    onClick={handleConfirm}
                    disabled={isConfirmDisabled}
                    isLoading={isLoading}
                    loadingText={loadingText}
                    fullWidth
                    size="md"
                >
                    Confirm Unlock
                </Button>
            </DialogAwareButtonContainer>
        </DialogContent>
    );
}
